"use client";

import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { postContact } from "@/apis/queryFunctions/contact";
import { useFetchAppInfo } from "@/apis/queryFunctions/appInfo";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import styles from "./contact.module.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useTrans from "@/hooks/useTrans";
import { useLocalContext } from "@/setup/locale-provider";
import { Check, X } from "lucide-react";
import Image from "next/image";
import { useHeader } from "@/context/HeaderLayoutContext";
import AppBreadcrumbs from "@/components/Common/AppBreadcrumbs";
import { Description } from "@/components/Common/Typography";

interface ContactClientProps {
  detail: any;
  locale: string;
}

export default function ContactClient({ detail, locale }: ContactClientProps) {
  const trans = useTrans();
  const { getLabel } = useLocalContext();
  const { data: appInfo } = useFetchAppInfo();
  const [activeMapIndex, setActiveMapIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedStatus, setSubmittedStatus] = useState<
    "success" | "error" | null
  >(null);
  const [submittedData, setSubmittedData] = useState<any>(null);

  const [formData, setFormData] = useState({
    ho_ten: "",
    dien_thoai: "",
    email: "",
    dia_chi: "",
    noi_dung: "",
  });

  const [errors, setErrors] = useState<any>({});

  // Auto-dismiss notification after 5 seconds
  React.useEffect(() => {
    if (submittedStatus) {
      const timer = setTimeout(() => {
        setSubmittedStatus(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [submittedStatus]);

  const validate = () => {
    const newErrors: any = {};
    if (!formData.ho_ten.trim()) {
      newErrors.ho_ten =
        locale === "vi" ? "Vui lòng nhập họ tên" : "Please enter your full name";
    }

    if (!formData.email.trim()) {
      newErrors.email =
        locale === "vi" ? "Vui lòng nhập email" : "Please enter your email";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
      newErrors.email =
        locale === "vi" ? "Email không hợp lệ" : "Invalid email address";
    }

    if (!formData.dien_thoai.trim()) {
      newErrors.dien_thoai =
        locale === "vi"
          ? "Vui lòng nhập số điện thoại"
          : "Please enter your phone number";
    } else {
      const phoneClean = formData.dien_thoai.trim();
      const vnPhoneRegex = /^0[35789]\d{8}$/;
      if (!vnPhoneRegex.test(phoneClean)) {
        newErrors.dien_thoai =
          locale === "vi"
            ? "Số điện thoại không hợp lệ (gồm 10 số, VD: 0912345678)"
            : "Invalid phone number (10 digits)";
      }
    }

    if (!formData.noi_dung.trim()) {
      newErrors.noi_dung =
        locale === "vi" ? "Vui lòng nhập nội dung" : "Please enter your message";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const contactMutation = useMutation({
    mutationFn: postContact,
    onSuccess: (rs: any) => {
      console.log("Contact submission response:", rs);
      setSubmittedData({ ...formData });
      setSubmittedStatus("success");
      setFormData({
        ho_ten: "",
        dien_thoai: "",
        email: "",
        dia_chi: "",
        noi_dung: "",
      });
      setIsSubmitting(false);
    },
    onError: (err: any) => {
      console.error("Contact submission error:", err);
      toast.error(
        locale === "vi"
          ? "Đã xảy ra lỗi khi gửi. Vui lòng thử lại!"
          : "An error occurred during submission. Please try again!"
      );
      setSubmittedStatus("error");
      setIsSubmitting(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setIsSubmitting(true);
      contactMutation.mutate(formData);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "dien_thoai") {
      const sanitized = value.replace(/\D/g, "").slice(0, 11);
      setFormData((prev) => ({ ...prev, [name]: sanitized }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: "" }));
    }
  };

  const { setHeaderStyle } = useHeader();

  React.useEffect(() => {
    setHeaderStyle({ transparent: true });
    return () => setHeaderStyle({ transparent: false });
  }, [setHeaderStyle]);

  const breadcrumbs = [
    { title: locale === "vi" ? "Trang chủ" : "Home", href: `/${locale}` },
    { title: locale === "vi" ? "Liên hệ" : "Contact", href: "#" },
  ];

  const bannerImg = detail?.picture
    ? appendImageUrlFromAPI({ src: detail.picture, size: "2k" })
    : "/images/new_tuyen_dung.png";

  const contactImage = appendImageUrlFromAPI({
    src: detail?.add_on_data_3?.[0]?.picture,
  });

  return (
    <div className={styles.wrapper}>
      {/* Hero Banner */}
      <section className={styles.hero}>
        <div className={styles.heroBg}>
          <Image
            src={bannerImg}
            alt={locale === "vi" ? "Liên hệ" : "Contact"}
            fill
            priority
            sizes="100vw"
            className={styles.bannerBgImage}
          />
          <div className={styles.heroMainContent}>
            <h1 className={styles.heroTitle}>
              {locale === "vi" ? "Liên hệ" : "Contact"}
            </h1>
            {detail?.description && (
              <Description
                as="p"
                size="medium"
                color="rgba(255, 255, 255, 0.95)"
                align="center"
                className={styles.heroDesc}
              >
                {detail.description}
              </Description>
            )}
          </div>
        </div>
      </section>

      <div className={styles.container}>
        <div style={{ marginTop: "16px", marginBottom: "24px" }}>
          <AppBreadcrumbs items={breadcrumbs} isLoading={false} />
        </div>

        <div className={styles.grid}>
          {/* Left: Info Card */}
          <div className={styles.infoCard}>
            <div
              className={styles.infoBg}
              style={{ backgroundImage: `url(${contactImage})` }}
            />
            <div className={styles.infoOverlay}>
              <div className={styles.infoContent}>
                <div className={styles.infoList}>
                  {detail?.add_on_data_2
                    ?.filter((el: any) => el.shared)
                    .map((item: any, idx: number) => (
                      <div key={idx} className={styles.infoItem}>
                        <span className={styles.infoLabel}>{item.title}</span>
                        <span className={styles.infoValue}>{item.summary}</span>
                      </div>
                    ))}
                  <div className={styles.infoItem}>
                    <span className={styles.infoValue}>
                      <b>Email:</b> {appInfo?.email}
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoValue}>
                      <b>Văn phòng:</b>{" "}
                      {locale === "vi" ? appInfo?.address : appInfo?.address_en}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form Card */}
          <div className={styles.formCard}>
            <div className={styles.formHeader}>
              <h1 className={styles.formTitle}>
                {detail?.meta_title || "Liên hệ"}
              </h1>
              <p className={styles.formDesc}>{detail?.meta_description}</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className={styles.inputField}>
                <label>
                  {trans?.contact?.placeholder?.fullName || "Họ và tên"} *
                </label>
                <input
                  type="text"
                  name="ho_ten"
                  value={formData.ho_ten}
                  onChange={handleChange}
                  placeholder={trans?.contact?.placeholder?.fullName}
                />
                {errors.ho_ten && (
                  <span className={styles.errorMsg}>{errors.ho_ten}</span>
                )}
              </div>
              <div className={styles.inputField}>
                <label>
                  {trans?.contact?.placeholder?.phone || "Số điện thoại"} *
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={11}
                  name="dien_thoai"
                  value={formData.dien_thoai}
                  onChange={handleChange}
                  placeholder={
                    locale === "vi"
                      ? "Số điện thoại liên hệ *"
                      : "Contact phone *"
                  }
                />
                {errors.dien_thoai && (
                  <span className={styles.errorMsg}>{errors.dien_thoai}</span>
                )}
              </div>

              <div className={styles.inputField}>
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={
                    locale === "vi" ? "Địa chỉ email *" : "Email address *"
                  }
                />
                {errors.email && (
                  <span className={styles.errorMsg}>{errors.email}</span>
                )}
              </div>

              <div className={styles.inputField}>
                <label>Địa chỉ</label>
                <input
                  type="text"
                  name="dia_chi"
                  value={formData.dia_chi}
                  onChange={handleChange}
                  placeholder="Địa chỉ liên hệ"
                />
              </div>

              <div className={styles.inputField}>
                <label>Ý kiến *</label>
                <textarea
                  name="noi_dung"
                  rows={3}
                  value={formData.noi_dung}
                  onChange={handleChange}
                  placeholder="Nhập nội dung ở đây..."
                />
                {errors.noi_dung && (
                  <span className={styles.errorMsg}>{errors.noi_dung}</span>
                )}
              </div>

              <button
                type="submit"
                className={styles.submitBtn}
                disabled={isSubmitting}
              >
                {isSubmitting ? "ĐANG GỬI..." : "GỬI THÔNG TIN"}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom: Map Section */}
        <div className={styles.mapSection}>
          <div className={styles.mapHeader}>
            <h2 className={styles.mapTitle}>DANH SÁCH NHÀ MÁY VIỆT THIÊN</h2>
          </div>

          <div className={styles.mapGrid}>
            <div className={styles.locationList}>
              {detail?.add_on_data
                ?.filter((el: any) => el.shared)
                .map((item: any, i: number) => (
                  <div
                    key={i}
                    className={`${styles.locationItem} ${activeMapIndex === i ? styles.activeLocation : ""}`}
                    onClick={() => setActiveMapIndex(i)}
                  >
                    <div className={styles.locTitle}>{item.title}</div>
                    <div className={styles.locAddr}>{item.summary}</div>
                    {item.content_2 && (
                      <div className={styles.locPhone}>
                        <span>SĐT:</span> {item.content_2}
                      </div>
                    )}
                  </div>
                ))}
            </div>
            <div className={styles.mapContent}>
              <div
                className={styles.mapIframe}
                dangerouslySetInnerHTML={{
                  __html: detail?.add_on_data?.[activeMapIndex]?.content,
                }}
              />
            </div>
          </div>
        </div>
        <ToastContainer position="bottom-right" />

        {submittedStatus && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              {/* Left Column: Slanted banner with checkmark & sparkles */}
              <div
                className={`${styles.modalLeft} ${
                  submittedStatus === "success"
                    ? styles.modalLeftSuccess
                    : styles.modalLeftError
                }`}
              >
                <div className={styles.emojiWrapper}>
                  {submittedStatus === "success" ? (
                    <>
                      <svg
                        className={styles.svgCheckmark}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 52 52"
                      >
                        <path
                          fill="none"
                          stroke="#12854f"
                          strokeWidth="5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M14 27l7 7 17-17"
                        />
                      </svg>
                      {/* Firework Sparkles */}
                      <span
                        className={`${styles.sparkle} ${styles.sparkle1}`}
                      />
                      <span
                        className={`${styles.sparkle} ${styles.sparkle2}`}
                      />
                      <span
                        className={`${styles.sparkle} ${styles.sparkle3}`}
                      />
                      <span
                        className={`${styles.sparkle} ${styles.sparkle4}`}
                      />
                      <span
                        className={`${styles.sparkle} ${styles.sparkle5}`}
                      />
                      <span
                        className={`${styles.sparkle} ${styles.sparkle6}`}
                      />
                      <span
                        className={`${styles.sparkle} ${styles.sparkle7}`}
                      />
                      <span
                        className={`${styles.sparkle} ${styles.sparkle8}`}
                      />
                    </>
                  ) : (
                    <svg
                      className={styles.svgError}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 52 52"
                    >
                      <path
                        className={styles.svgErrorPath1}
                        fill="none"
                        stroke="#e53e3e"
                        strokeWidth="5"
                        strokeLinecap="round"
                        d="M16 16l20 20"
                      />
                      <path
                        className={styles.svgErrorPath2}
                        fill="none"
                        stroke="#e53e3e"
                        strokeWidth="5"
                        strokeLinecap="round"
                        d="M36 16L16 36"
                      />
                    </svg>
                  )}
                </div>
                <div className={styles.leftBgDecoration} />
              </div>

              {/* Right Column: Title, Message, Data Mockup */}
              <div className={styles.modalRight}>
                {/* Close Button X at Top Right */}
                <button
                  type="button"
                  className={styles.modalCloseBtn}
                  onClick={() => setSubmittedStatus(null)}
                >
                  <X size={16} />
                </button>

                <div>
                  <h3 className={styles.modalTitle}>
                    {submittedStatus === "success"
                      ? locale === "vi"
                        ? "Gửi thành công!"
                        : "Well Done!"
                      : locale === "vi"
                        ? "Gửi thất bại!"
                        : "Submission Failed!"}
                  </h3>

                  <p className={styles.modalDesc}>
                    {submittedStatus === "success"
                      ? locale === "vi"
                        ? "Cảm ơn bạn đã liên hệ với Viet Thien. Chúng tôi đã tiếp nhận thông tin và sẽ phản hồi lại bạn trong thời gian sớm nhất."
                        : "Thank you for contacting Viet Thien. We have received your information and will respond to you as soon as possible."
                      : locale === "vi"
                        ? "Đã xảy ra lỗi trong quá trình gửi. Vui lòng thử lại sau."
                        : "An error occurred during submission. Please try again later."}
                  </p>
                </div>

                <div className={styles.modalFooter}>
                  <button
                    type="button"
                    className={styles.modalActionBtn}
                    onClick={() => setSubmittedStatus(null)}
                  >
                    {locale === "vi" ? "Đóng" : "Close"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
